import { Fragment, type ReactNode } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { CategoryGroup } from "../utils/groupByCategory";

/**
 * A table split into collapsible category sections. Expansion is controlled
 * by the parent so it can offer "Expand all / Collapse all" and force groups
 * open while a search is active.
 */
export function GroupedTable<T extends { id: number }>({
  columns,
  groups,
  isExpanded,
  onToggle,
  empty,
  rowClassName,
}: {
  columns: { key: string; title: string; render: (row: T) => ReactNode }[];
  groups: CategoryGroup<T>[];
  isExpanded: (category: string) => boolean;
  onToggle: (category: string) => void;
  empty: string;
  rowClassName?: (row: T) => string;
}) {
  if (groups.length === 0) {
    return <div className="px-4 py-12 text-center text-sm text-tribal-muted">{empty}</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-left text-sm">
        <thead className="text-xs uppercase tracking-wide text-tribal-muted">
          <tr className="border-b border-tribal-border">
            {columns.map((c) => (
              <th key={c.key} className="px-4 py-3 font-medium">
                {c.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => {
            const open = isExpanded(group.category);
            return (
              <Fragment key={group.category}>
                <tr className="border-b border-tribal-border bg-white/[0.03]">
                  <td colSpan={columns.length} className="p-0">
                    <button
                      type="button"
                      onClick={() => onToggle(group.category)}
                      aria-expanded={open}
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-left font-medium text-white hover:bg-white/[0.04]"
                    >
                      {open ? (
                        <ChevronDown className="h-4 w-4 text-tribal-muted" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-tribal-muted" />
                      )}
                      {group.category}
                      <span className="text-xs font-normal text-tribal-muted">
                        ({group.rows.length})
                      </span>
                    </button>
                  </td>
                </tr>
                {open &&
                  group.rows.map((row) => (
                    <tr
                      key={row.id}
                      className={`border-b border-tribal-border/60 hover:bg-white/[0.02] ${rowClassName?.(row) ?? ""}`}
                    >
                      {columns.map((c) => (
                        <td key={c.key} className="px-4 py-2">
                          {c.render(row) ?? "-"}
                        </td>
                      ))}
                    </tr>
                  ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
